import React from 'react'
import PropTypes from 'prop-types'
import {cn} from '@/lib/utils'
import {CircleCheckSolid} from 'hec/assets/icons'
import {Heading} from '@/components/ui/heading'
import {BodyCopy} from '@/components/ui/body-copy'
import {Table, TableBody, TableCell, TableHead, TableRow} from '@/components/ui/table'
import {goalPropType} from './utils'
import {ExpandContent} from 'hec/components/expand-content'

const GoalBenefitsTable = ({currentGoal, bgImage, isMobile, benefits, goals, disclaimerText}) => {
    const classes = {
        tableRow:
            'flex justify-start h-10 sm:h-16 lg:h-20 lg:justify-center border-none my-0.5 lg:my-2',
        tableHead: 'rounded-lg flex items-center text-primary h-full sm:p-3 p-1',
        tableCell:
            'rounded-lg flex items-center sm:p-3 p-1 justify-center w-2/12 text-center mx-0.5 lg:mx-1',
        text: cn('font-medium mx-auto text-[.4rem] lg:text-[.65rem] leading-tight', {
            'text-[.3rem] leading-tight': isMobile
        })
    }

    const benefitsTable = (
        <Table className="table-fixed" id="benefitsTable" data-testid="goalBenefitsTable">
            <TableBody className="border-none" data-testid="goalBenefitsTable-TableBody">
                {benefits?.map((benefit, benefitIdx) => {
                    const benefitValue = benefit.data.benefit
                    return (
                        <TableRow
                            className={cn(classes.tableRow)}
                            key={`benefit-header-${benefitIdx}`}
                            data-testid="goalBenefitsTable-TableRow"
                        >
                            <TableHead
                                className={cn(
                                    classes.tableHead,
                                    'mr-0.5 lg:mr-1 theme-medium bg-background h-full w-1/4'
                                )}
                                data-testid="goalBenefitsTable-TableHead"
                            >
                                <p className={cn(classes.text, 'text-primary')}>{benefitValue}</p>
                            </TableHead>
                            {goals?.map((goal, goalIdx) => {
                                const goalBenefits = goal.data.benefits.map(
                                    (goalBenefit) => goalBenefit.benefit.value.data.benefit
                                )
                                const isBenefitGoalMatch = goalBenefits?.includes(benefitValue)
                                let isActiveBenefit = false

                                if (
                                    currentGoal &&
                                    isBenefitGoalMatch &&
                                    currentGoal.id == goal.data.id
                                ) {
                                    isActiveBenefit = true
                                }

                                return (
                                    <TableCell
                                        key={`benefit-goal-${goalIdx}`}
                                        className={cn(classes.tableCell, 'bg-gray-05/30', {
                                            'bg-active': isActiveBenefit
                                        })}
                                        data-testid="goalBenefitsTable-TableCell"
                                    >
                                        {isBenefitGoalMatch && (
                                            <CircleCheckSolid
                                                className={cn('w-full text-gray-02 h-3/4', {
                                                    'text-active-foreground': isActiveBenefit
                                                })}
                                            />
                                        )}
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    )
                })}

                {/* Bottom goal header */}
                <TableRow className={cn(classes.tableRow)} data-testid="goalBenefitsTable-TableRow">
                    <TableHead
                        key={`goal-table-none`}
                        className={cn(classes.tableHead, 'lg:mr-1 w-1/4 invisible')}
                        data-testid="goalBenefitsTable-TableHead"
                    />
                    {goals?.map((goal, i) => {
                        return (
                            <TableCell
                                key={`goal-table-${i}`}
                                className={cn(classes.tableCell, 'bg-accent')}
                                data-testid="goalBenefitsTable-TableCell"
                            >
                                <p className={cn(classes.text, 'text-white')}>
                                    {goal?.data?.title}
                                </p>
                            </TableCell>
                        )
                    })}
                </TableRow>
            </TableBody>
        </Table>
    )

    const heading = (
        <Heading
            tag="h4"
            variant="h4"
            className="my-4 text-center text-black"
            data-testid="goalBenefitsTable-Heading"
        >
            HSA Goals and Benefits
        </Heading>
    )
    return (
        <div
            className={cn('bg-cover bg-center bg-no-repeat p-4 lg:p-10', {'bg-gray-01': isMobile})}
            style={!isMobile ? {backgroundImage: `url(${bgImage})`} : {}}
        >
            {isMobile && heading}
            <div className="flex flex-col items-center px-2 py-4 lg:mx-20 lg:p-20 bg-white">
                {!isMobile && heading}
                {isMobile ? (
                    <ExpandContent
                        content={benefitsTable}
                        withModal={false}
                        data-testid="goalBenefitsTable-ExpandContent"
                    />
                ) : (
                    benefitsTable
                )}
                {disclaimerText && (
                    <div className="mt-2 ml-[15%] w-3/4">
                        {disclaimerText.map(({text}, idx) => (
                            <BodyCopy
                                size={isMobile ? 'xsmall' : 'small'}
                                className={`!font-medium mx-auto ${
                                    isMobile ? '!leading-3' : '!leading-6'
                                }`}
                                style={{fontSize: isMobile ? '.3rem' : '.65rem'}}
                                key={`disclaimer-text-${idx}`}
                                data-testid="goalBenefitsTable-BodyCopy"
                            >
                                {text}
                            </BodyCopy>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default GoalBenefitsTable

GoalBenefitsTable.propTypes = {
    attributes: PropTypes.object,
    currentGoal: goalPropType,
    goals: PropTypes.array,
    disclaimerText: PropTypes.array,
    benefits: PropTypes.array,
    bgImage: PropTypes.string,
    isMobile: PropTypes.bool.isRequired
}
